import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IPrintFileBatchDetails } from '../../api/printFileBatches';
import { computePackStats, fetchPackDetails } from '../../lib/printFileReview/packDetails';

/**
 * Server view of one pack (files, reviews, pipeline history) for moderators.
 * `version` — change it (e.g. the list's updatedAt) to fetch fresh data.
 */
export const usePackDetails = (batchId: string | null, enabled: boolean, version?: string) => {
    const [state, setState] = useState<{ details: IPrintFileBatchDetails | null; loading: boolean; error: string | null; at: number | null }>({
        details: null,
        loading: false,
        error: null,
        at: null,
    });
    const requestRef = useRef(0);
    const versionRef = useRef(version);

    const load = useCallback(async (force: boolean) => {
        if (!batchId || !enabled) return;
        const request = ++requestRef.current;
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const details = await fetchPackDetails(batchId, force ? 0 : undefined);
            if (request === requestRef.current) setState({ details, loading: false, error: null, at: Date.now() });
        } catch (e: any) {
            if (request === requestRef.current) setState((s) => ({ ...s, loading: false, error: e?.message || 'Failed to load set' }));
        }
    }, [batchId, enabled]);

    useEffect(() => {
        void load(false);
    }, [load]);

    // The pack changed on the server (list refresh) → fetch it again.
    useEffect(() => {
        if (versionRef.current === version) return;
        versionRef.current = version;
        void load(true);
    }, [version, load]);

    const stats = useMemo(() => (state.details ? computePackStats(state.details) : null), [state.details]);
    return { ...state, stats, reload: () => load(true) };
};
